import { useMemo } from 'react';
import { Badge, Group, Text, Tooltip } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import type { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import type { MaintenanceDueItem } from '../../api/types';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { formatCounter, formatDate } from '../../lib/format';

/**
 * What is due, and how close it is.
 *
 * A task can be limited by hours, by the calendar, or by both, and "whichever comes first"
 * wins, so the remaining column shows the binding limit rather than a single number.
 */
export function DueItemsTable({
  items,
  isLoading,
  showRegistration = false,
  onRowClick,
}: {
  items: MaintenanceDueItem[];
  isLoading?: boolean;
  showRegistration?: boolean;
  onRowClick?: (item: MaintenanceDueItem) => void;
}) {
  const { t } = useTranslation();

  const columns = useMemo<ColumnDef<MaintenanceDueItem, unknown>[]>(() => {
    const base: ColumnDef<MaintenanceDueItem, unknown>[] = [
      {
        accessorKey: 'task_name',
        header: t('maintenance.task'),
        cell: ({ row }) => (
          <Group gap={6} wrap="nowrap">
            {row.original.grounds_aircraft ? (
              <Tooltip label={t('maintenance.groundsAircraft')}>
                <IconAlertTriangle size={14} color="var(--mantine-color-red-6)" />
              </Tooltip>
            ) : null}
            <div style={{ minWidth: 0 }}>
              <Text fz="sm" truncate="end">
                {row.original.task_name}
              </Text>
              <Text fz="xs" c="dimmed">
                {row.original.task_code} · {t(`maintenance.${row.original.area}`)}
              </Text>
            </div>
          </Group>
        ),
      },
      {
        accessorKey: 'interval_display',
        header: t('maintenance.interval'),
        meta: { width: 120 },
        cell: ({ row }) => (
          <Text fz="xs" c="dimmed">
            {row.original.interval_display}
          </Text>
        ),
      },
      {
        accessorKey: 'last_done_on',
        header: t('maintenance.lastDone'),
        meta: { width: 110 },
        cell: ({ row }) => (
          <div>
            <Text fz="sm">{formatDate(row.original.last_done_on)}</Text>
            {row.original.last_done_at_counter ? (
              <Text fz="xs" c="dimmed" ff="monospace">
                {formatCounter(row.original.last_done_at_counter)}
              </Text>
            ) : null}
          </div>
        ),
      },
      {
        id: 'due',
        header: t('maintenance.due'),
        meta: { width: 130 },
        cell: ({ row }) => (
          <div>
            {row.original.due_at_counter ? (
              <Text fz="sm" ff="monospace">
                {formatCounter(row.original.due_at_counter)}
              </Text>
            ) : null}
            {row.original.due_on ? (
              <Text fz="xs" c="dimmed">
                {formatDate(row.original.due_on)}
              </Text>
            ) : null}
          </div>
        ),
      },
      {
        accessorKey: 'remaining_display',
        header: t('maintenance.remaining'),
        meta: { align: 'right', width: 130 },
        cell: ({ row }) => (
          <Text
            fz="sm"
            fw={600}
            ff="monospace"
            c={
              row.original.status === 'OVERDUE'
                ? 'red'
                : row.original.status === 'DUE' || row.original.status === 'IN_TOLERANCE'
                  ? 'orange'
                  : undefined
            }
          >
            {row.original.remaining_display}
          </Text>
        ),
      },
      {
        accessorKey: 'status',
        header: t('common.status'),
        meta: { width: 140 },
        cell: ({ row }) => (
          <Group gap={4} wrap="nowrap">
            <StatusBadge kind="due" value={row.original.status} />
            {row.original.is_blocking ? (
              <Badge size="xs" color="red" variant="filled">
                {t('aircraft.GROUNDED')}
              </Badge>
            ) : null}
          </Group>
        ),
      },
    ];

    if (showRegistration) {
      base.unshift({
        accessorKey: 'registration',
        header: t('aircraft.registration'),
        meta: { mono: true, width: 100 },
        cell: ({ row }) => (
          <Text fw={600} className="identifier">
            {row.original.registration}
          </Text>
        ),
      });
    }
    return base;
  }, [t, showRegistration]);

  return (
    <DataTable
      data={items}
      columns={columns}
      isLoading={isLoading}
      onRowClick={onRowClick}
      rowProps={(item) =>
        item.status === 'OVERDUE'
          ? { bg: 'var(--mantine-color-red-light)' }
          : item.status === 'DUE' || item.status === 'IN_TOLERANCE'
            ? { bg: 'var(--mantine-color-orange-light)' }
            : {}
      }
      emptyTitle={t('maintenance.dueList')}
      emptyHint={t('maintenance.OK')}
      minWidth={showRegistration ? 1000 : 900}
    />
  );
}
